import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useCompany } from '../contexts/CompanyContext';
import { useCompanyMembership } from '../contexts/CompanyMembershipContext';
import { Edit as EditIcon, Delete as DeleteIcon, Work as WorkIcon, PictureAsPdf as PdfIcon, Create as CreateIcon, CheckCircle as CheckCircleIcon, Link as LinkIcon } from '@mui/icons-material';
import { ensurePortalTokenForEstimate, ensureSigningTokenForEstimate, buildPortalUrl } from '../utils/estimateUtils';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import SignatureCapture from '../components/estimates/SignatureCapture';
import { ensureHttps } from '../utils/urlUtils';

const STATUS_COLORS = {
  'Created': 'default',
  'Sent': 'info',
  'Approved': 'success',
  'Rejected': 'error',
  'Converted to Job': 'warning'
};

// Helper to get customer info (address, phone) if available
function getCustomerDetails(estimate) {
  const info = estimate.contactInfo || {};
  return {
    email: info.email || '',
    phone: info.phone || '',
    address: [info.streetAddress, info.city, info.zipCode].filter(Boolean).join(', ')
  };
}

// Custom PDF content component
function EstimatePDFContent({ estimate }) {
  const { companyProfile } = useCompany();
  const customer = getCustomerDetails(estimate);
  const primaryColor = '#1976d2'; // Primary blue color
  const estimateDate = new Date(estimate.createdAt).toLocaleDateString('en-US', { 
    month: '2-digit', 
    day: '2-digit', 
    year: 'numeric' 
  });
  const dueDate = estimate.dueDate 
    ? new Date(estimate.dueDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : new Date(new Date(estimate.createdAt).getTime() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  
  // Calculate subtotal and tax
  const subtotal = estimate.lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  // Use estimate tax rate if set, otherwise fall back to company default
  const taxRate = estimate.taxRate || companyProfile?.defaultTaxRate || 0;
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;

  // Letter size: 612pt width, 792pt height
  // With 24pt margins: 564pt content width, 744pt content height
  const margin = 24;
  const contentWidth = 612 - (2 * margin);
  const contentHeight = 792 - (2 * margin);
  return (
    <div style={{ width: contentWidth, minHeight: contentHeight, padding: margin, fontFamily: 'Arial, sans-serif', color: '#222', background: '#fff', boxSizing: 'border-box' }}>
      {/* Header Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        {/* Left: Company Info */}
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 'bold', color: '#222', textTransform: 'uppercase' }}>
            {companyProfile?.companyName?.toUpperCase() || 'YOUR COMPANY INC.'}
          </h1>
          <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
            {companyProfile?.address && <>{companyProfile.address}<br /></>}
            {companyProfile?.phone && <>{companyProfile.phone}<br /></>}
            {companyProfile?.email && <>{companyProfile.email}<br /></>}
            {companyProfile?.website && <>{companyProfile.website}<br /></>}
            {companyProfile?.licenseNumber && <>License #: {companyProfile.licenseNumber}</>}
          </div>
        </div>
        
        {/* Right: Logo and Title */}
        <div style={{ textAlign: 'right', flex: 1 }}>
          {/* Logo */}
          {companyProfile?.logo ? (
            <div style={{ 
              marginLeft: 'auto', 
              marginBottom: 12,
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <img 
                src={companyProfile.logo.startsWith('data:') ? companyProfile.logo : ensureHttps(companyProfile.logo)}
                alt="Company logo"
                style={{
                  maxWidth: 120,
                  maxHeight: 60,
                  objectFit: 'contain'
                }}
              />
            </div>
          ) : (
            <div style={{ 
              width: 120, 
              height: 60, 
              border: '1px dashed #ccc', 
              borderRadius: 4, 
              marginLeft: 'auto', 
              marginBottom: 12,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              color: '#999'
            }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>☁</div>
              <div>Upload Logo</div>
            </div>
          )}
          
          {/* ESTIMATE PROPOSAL Title */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: primaryColor, lineHeight: 1.2, letterSpacing: 1 }}>
              ESTIMATE
            </div>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: primaryColor, lineHeight: 1.2, letterSpacing: 1 }}>
              PROPOSAL
            </div>
          </div>
          
          {/* Estimate Details */}
          <div style={{ fontSize: 11, textAlign: 'right', lineHeight: 1.8 }}>
            <div>
              <span style={{ color: primaryColor, fontWeight: 'bold' }}>Estimate #</span>
              <span style={{ marginLeft: 8, color: '#222' }}>{estimate.estimateId}</span>
            </div>
            <div>
              <span style={{ color: primaryColor, fontWeight: 'bold' }}>Estimate date</span>
              <span style={{ marginLeft: 8, color: '#222' }}>{estimateDate}</span>
            </div>
            <div>
              <span style={{ color: primaryColor, fontWeight: 'bold' }}>Due date</span>
              <span style={{ marginLeft: 8, color: '#222' }}>{dueDate}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bill To Section */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 'bold', color: primaryColor }}>
          Bill To:
        </h3>
        <div style={{ fontSize: 12, color: '#222', lineHeight: 1.6 }}>
          <div style={{ fontWeight: 'bold' }}>{estimate.customerName}</div>
          {customer.address && <div>{customer.address}</div>}
          {customer.phone && <div>Phone: {customer.phone}</div>}
          {customer.email && <div>Email: {customer.email}</div>}
        </div>
      </div>

      {/* Line Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
        <thead>
          <tr style={{ background: primaryColor }}>
            <th style={{ padding: '10px 8px', textAlign: 'left', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>QTY</th>
            <th style={{ padding: '10px 8px', textAlign: 'left', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>Description</th>
            <th style={{ padding: '10px 8px', textAlign: 'right', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>Unit Price</th>
            <th style={{ padding: '10px 8px', textAlign: 'right', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {estimate.lineItems.map((item, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '10px 8px', fontSize: 11, textAlign: 'left' }}>{item.quantity.toFixed(2)}</td>
              <td style={{ padding: '10px 8px', fontSize: 11, textAlign: 'left' }}>{item.description || item.name}</td>
              <td style={{ padding: '10px 8px', fontSize: 11, textAlign: 'right' }}>${item.unitPrice.toFixed(2)}</td>
              <td style={{ padding: '10px 8px', fontSize: 11, textAlign: 'right' }}>${(item.quantity * item.unitPrice).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Summary Section */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <div style={{ width: 200, fontSize: 11 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {taxRate > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span>Sales Tax ({taxRate}%)</span>
              <span>${taxAmount.toFixed(2)}</span>
            </div>
          )}
          <div style={{ borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: primaryColor, fontWeight: 'bold' }}>Total (USD)</span>
            <span style={{ color: primaryColor, fontWeight: 'bold' }}>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 20 }}>
        {/* Terms and Conditions */}
        <div style={{ flex: 1, fontSize: 10, color: '#555' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 11, fontWeight: 'bold', color: primaryColor }}>
            Terms and Conditions
          </h4>
          <div style={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}>
            {companyProfile?.defaultTerms ? (
              companyProfile.defaultTerms
            ) : (
              <>
                <div>Payment is due in 14 days.</div>
                <div>Please make checks payable to: {companyProfile?.companyName || 'Your Company Inc.'}</div>
              </>
            )}
          </div>
        </div>

        {/* Customer Signature */}
        <div style={{ flex: 1, textAlign: 'center', paddingTop: 40 }}>
          {estimate.signatureUrl ? (
            <>
              <img
                src={estimate.signatureUrl.startsWith('data:') ? estimate.signatureUrl : ensureHttps(estimate.signatureUrl)}
                alt="Customer signature"
                style={{
                  maxWidth: '200px',
                  maxHeight: '80px',
                  border: '1px solid #ddd',
                  padding: '4px',
                  marginBottom: '8px',
                  display: 'block',
                  margin: '0 auto 8px auto'
                }}
                crossOrigin={estimate.signatureUrl.startsWith('data:') ? undefined : "anonymous"}
              />
              <div style={{ fontSize: 10, color: primaryColor, fontWeight: 'bold', marginBottom: '4px' }}>
                Customer Signature
              </div>
              {estimate.signedByName && (
                <div style={{ fontSize: 9, color: '#555', marginBottom: '2px' }}>
                  {estimate.signedByName}
                </div>
              )}
              {estimate.signedAt && (
                <div style={{ fontSize: 9, color: '#555' }}>
                  {new Date(estimate.signedAt).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ borderTop: '1px solid #ddd', width: 200, margin: '0 auto 8px auto', paddingTop: 8 }}></div>
              <div style={{ fontSize: 10, color: primaryColor, fontWeight: 'bold' }}>
                Customer Signature
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EstimateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { estimates, deleteEstimate, updateEstimate, addJob } = useData();
  const { companyProfile } = useCompany();
  const { companyId } = useCompanyMembership();
  const estimate = estimates.find(e => e.id === id);
  const pdfRef = useRef();
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [portalLinkCopied, setPortalLinkCopied] = useState(false);

  // Helper function to auto-create job from approved estimate
  const autoCreateJobFromEstimate = async (estimateData) => {
    const autoCreateEnabled = companyProfile?.automationSettings?.autoCreateJobFromApproved ?? true;
    
    if (!autoCreateEnabled || estimateData.jobId) {
      return null; // Auto-create disabled or job already exists
    }

    try {
      // Prepare work items with default values
      const workItems = (estimateData.lineItems || []).map(item => ({
        name: item.name || item.description || 'Work Item',
        description: item.description || '',
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || 0,
        total: (item.quantity || 1) * (item.unitPrice || 0),
        status: 'Not Started'
      }));

      // Prepare contact info with default values
      const contactInfo = {
        email: estimateData.contactInfo?.email || '',
        phone: estimateData.contactInfo?.phone || '',
        streetAddress: estimateData.contactInfo?.streetAddress || '',
        city: estimateData.contactInfo?.city || '',
        zipCode: estimateData.contactInfo?.zipCode || ''
      };

      const newJob = await addJob({
        customerId: estimateData.customerId,
        customerName: estimateData.customerName || 'Unknown Customer',
        estimateId: estimateData.id,
        description: estimateData.description || '',
        total: estimateData.total || 0,
        status: 'Not Started',
        createdAt: new Date().toISOString(),
        createdBy: 'Admin',
        lineItems: estimateData.lineItems || [],
        contactInfo: contactInfo,
        workItems: workItems,
        notes: [`Job auto-created from approved estimate ${estimateData.estimateId}`],
        address: estimateData.contactInfo?.streetAddress || '',
        remainingBalance: estimateData.total || 0,
        totalPaid: 0,
        taxRate: estimateData.taxRate
      });
      
      // Update estimate with job ID
      await updateEstimate(estimateData.id, {
        ...estimateData,
        jobId: newJob.id,
        status: 'Converted to Job',
        updatedAt: new Date().toISOString()
      });

      return newJob;
    } catch (error) {
      console.error('Error auto-creating job from estimate:', error);
      // Don't throw - allow estimate approval to complete even if job creation fails
      return null;
    }
  };

  if (!estimate) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Estimate not found</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/estimates')}
          sx={{ mt: 2 }}
        >
          Back to Estimates
        </Button>
      </Box>
    );
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this estimate?')) {
      deleteEstimate(estimate.id);
      navigate('/estimates');
    }
  };

  const handleSaveSignature = async (signatureData) => {
    try {
      console.log('handleSaveSignature called with:', signatureData);
      console.log('Updating estimate:', estimate.id);
      
      // Update estimate with signature data and set status to Approved
      const updatedEstimate = {
        ...estimate,
        signatureUrl: signatureData.signatureUrl,
        signedAt: signatureData.signedAt,
        signedByName: signatureData.signedByName,
        status: 'Approved',
        updatedAt: new Date().toISOString()
      };
      
      const result = await updateEstimate(estimate.id, updatedEstimate);
      
      console.log('Estimate updated successfully:', result);
      
      // Auto-create job if enabled
      const newJob = await autoCreateJobFromEstimate(updatedEstimate);
      if (newJob) {
        console.log('Job auto-created from approved estimate:', newJob.id);
        // Optionally show a notification or navigate to job
      }
      
      // The estimate object will be updated via the context
      // No need to manually refresh since updateEstimate updates the context
    } catch (error) {
      console.error('Error saving signature in handleSaveSignature:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      // Show user-friendly error
      const errorMessage = error.message || 'Failed to save signature. Please check the console for details.';
      alert(`Error: ${errorMessage}`);
      throw error; // Re-throw so SignatureCapture can handle it
    }
  };

  const handleCopyPortalLink = async () => {
    try {
      if (!companyId) {
        alert('Company ID not available. Please refresh the page and try again.');
        return;
      }

      // Ensure both portal token and signing token exist (portal serves as signing link)
      const [portalToken, signingToken] = await Promise.all([
        ensurePortalTokenForEstimate(companyId, estimate.id),
        ensureSigningTokenForEstimate(companyId, estimate.id)
      ]);
      
      // Build portal URL
      const portalUrl = buildPortalUrl(portalToken);

      // Copy to clipboard
      await navigator.clipboard.writeText(portalUrl);
      setPortalLinkCopied(true);
      setTimeout(() => setPortalLinkCopied(false), 3000);

      // Show success message with option to email
      const userConfirmed = window.confirm(
        `Customer Portal link copied to clipboard!\n\nShare this link with your customer:\n${portalUrl}\n\nYour customer can view, download, and sign the estimate from this portal.\n\nClick OK to open email client, or Cancel to just copy the link.`
      );
      
      if (userConfirmed && estimate.contactInfo?.email) {
        const subject = encodeURIComponent(`Your Estimate #${estimate.estimateId} - Customer Portal`);
        const body = encodeURIComponent(
          `Hello,\n\nPlease review your estimate #${estimate.estimateId} in the customer portal:\n${portalUrl}\n\nYou can view the estimate details, download a PDF, and sign it directly from the portal.\n\nThank you!`
        );
        window.location.href = `mailto:${estimate.contactInfo.email}?subject=${subject}&body=${body}`;
      }
    } catch (error) {
      console.error('Error generating portal link:', error);
      alert('Failed to generate portal link. Please try again.');
    }
  };

  const handleApprove = async () => {
    if (window.confirm('Are you sure you want to approve this estimate?')) {
      try {
        // First update estimate status
        const updatedEstimate = {
          ...estimate,
          status: 'Approved',
          updatedAt: new Date().toISOString()
        };
        
        await updateEstimate(estimate.id, updatedEstimate);

        // Auto-create job if enabled, otherwise ask user
        const autoCreateEnabled = companyProfile?.automationSettings?.autoCreateJobFromApproved ?? true;
        let newJob = null;

        if (autoCreateEnabled && !estimate.jobId) {
          // Auto-create job
          newJob = await autoCreateJobFromEstimate(updatedEstimate);
          if (newJob) {
            // Navigate to the new job's details page
            navigate(`/jobs/${newJob.id}`);
            return;
          }
        } else if (!estimate.jobId) {
          // Manual creation (existing flow)
          newJob = await addJob({
            customerId: updatedEstimate.customerId,
            customerName: updatedEstimate.customerName,
            estimateId: updatedEstimate.id,
            description: updatedEstimate.description,
            total: updatedEstimate.total,
            status: 'Not Started',
            createdAt: new Date().toISOString(),
            createdBy: 'Admin',
            lineItems: updatedEstimate.lineItems,
            contactInfo: updatedEstimate.contactInfo,
            workItems: updatedEstimate.lineItems.map(item => ({
              name: item.description,
              description: item.description,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              total: item.total,
              status: 'Not Started'
            })),
            notes: [`Job created from approved estimate ${updatedEstimate.estimateId}`]
          });

          // Update estimate with job ID
          await updateEstimate(updatedEstimate.id, {
            ...updatedEstimate,
            jobId: newJob.id,
            status: 'Converted to Job'
          });

          // Navigate to the new job's details page
          navigate(`/jobs/${newJob.id}`);
        }
      } catch (error) {
        console.error('Error approving estimate:', error);
        alert(error.message || 'Failed to approve estimate. Please try again.');
      }
    }
  };

  const handleReject = () => {
    if (window.confirm('Are you sure you want to reject this estimate?')) {
      updateEstimate(estimate.id, {
        ...estimate,
        status: 'Rejected',
        updatedAt: new Date().toISOString()
      });
    }
  };

  const handleCreateJob = async () => {
    if (window.confirm('Are you sure you want to create a job from this estimate?')) {
      try {
        // Prepare work items with default values
        const workItems = (estimate.lineItems || []).map(item => ({
          name: item.description || 'Work Item',
          description: item.description || '',
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || 0,
          total: (item.quantity || 1) * (item.unitPrice || 0),
          status: 'Not Started'
        }));

        // Prepare contact info with default values
        const contactInfo = {
          email: estimate.contactInfo?.email || '',
          phone: estimate.contactInfo?.phone || '',
          streetAddress: estimate.contactInfo?.streetAddress || '',
          city: estimate.contactInfo?.city || '',
          zipCode: estimate.contactInfo?.zipCode || ''
        };

        const newJob = await addJob({
          customerId: estimate.customerId,
          customerName: estimate.customerName || 'Unknown Customer',
          estimateId: estimate.id,
          description: estimate.description || '',
          total: estimate.total || 0,
          status: 'Not Started',
          createdAt: new Date().toISOString(),
          createdBy: 'Admin',
          lineItems: estimate.lineItems || [],
          contactInfo: contactInfo,
          workItems: workItems,
          notes: [`Job created from estimate ${estimate.estimateId}`],
          address: estimate.contactInfo?.streetAddress || '',
          remainingBalance: estimate.total || 0,
          totalPaid: 0
        });
        
        // Update estimate status to "Converted to Job"
        await updateEstimate(estimate.id, {
          ...estimate,
          status: 'Converted to Job',
          jobId: newJob,
          updatedAt: new Date().toISOString()
        });

        // Navigate to the new job's details page
        navigate(`/jobs/${newJob}`);
      } catch (error) {
        console.error('Error converting estimate to job:', error);
        alert(error.message || 'Failed to convert estimate to job. Please try again.');
      }
    }
  };

  // Helper function to preload images
  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = ensureHttps(src);
    });
  };

  const handleGeneratePDF = async () => {
    if (!pdfRef.current) {
      console.error('PDF ref is not available');
      return;
    }

    try {
      // Preload signature image if it exists
      if (estimate.signatureUrl) {
        try {
          await preloadImage(estimate.signatureUrl);
        } catch (error) {
          console.warn('Failed to preload signature image:', error);
        }
      }

      // US Letter size
      const pageWidth = 612;
      const pageHeight = 792;
      const margin = 24; // Reduced margins
      const contentWidth = pageWidth - 2 * margin;
      const contentHeight = pageHeight - 2 * margin;

      // Prepare the PDF content container
      const pdfContent = pdfRef.current;
      const originalDisplay = pdfContent.style.display;
      pdfContent.style.display = 'block';
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.top = '0';
      pdfContent.style.width = contentWidth + 'px';
      pdfContent.style.height = contentHeight + 'px';
      pdfContent.style.padding = '0';
      pdfContent.style.margin = '0';
      pdfContent.style.boxSizing = 'border-box';
      document.body.appendChild(pdfContent);

      // Wait a bit for any images to render
      await new Promise(resolve => setTimeout(resolve, 500));

      // Render to canvas at scale 2 for quality
      const scale = 2;
      const canvas = await html2canvas(pdfContent, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: contentWidth,
        height: contentHeight,
        windowWidth: contentWidth,
        windowHeight: contentHeight,
        logging: false,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector('[data-pdf-content]');
          if (clonedElement) {
            clonedElement.style.width = contentWidth + 'px';
            clonedElement.style.height = 'auto';
            clonedElement.style.minHeight = contentHeight + 'px';
            clonedElement.style.display = 'block';
            clonedElement.style.background = '#fff';
            clonedElement.style.padding = '24px';
            clonedElement.style.boxSizing = 'border-box';
            
            // Ensure signature images have proper src
            const signatureImgs = clonedElement.querySelectorAll('img[alt="Customer signature"]');
            signatureImgs.forEach(img => {
              if (img.src && !img.src.startsWith('data:')) {
                img.crossOrigin = 'anonymous';
              }
            });
          }
        }
      });

      // Calculate scaling to fit letter size
      // Canvas is rendered at scale 2, so actual rendered size is canvas.width/scale
      const actualWidth = canvas.width / scale;
      const actualHeight = canvas.height / scale;
      
      // Scale to fit content area in points (PDF uses points)
      const scaleX = contentWidth / actualWidth;
      const scaleY = contentHeight / actualHeight;
      const scaleFactor = Math.min(scaleX, scaleY, 1); // Don't scale up, only down

      // Scale dimensions for PDF (in points)
      const scaledWidth = actualWidth * scaleFactor;
      const scaledHeight = actualHeight * scaleFactor;

      // Create PDF with letter size
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [pageWidth, pageHeight]
      });
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      // Center the image on the page
      const x = (pageWidth - scaledWidth) / 2;
      const y = margin;
      
      pdf.addImage(imgData, 'JPEG', x, y, scaledWidth, scaledHeight);
      pdf.save(`estimate-${estimate.estimateId}.pdf`);

      // Restore
      pdfContent.style.display = originalDisplay;
      pdfContent.style.position = '';
      pdfContent.style.left = '';
      pdfContent.style.top = '';
      pdfContent.style.width = '';
      pdfContent.style.height = '';
      document.body.removeChild(pdfContent);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please check the console for details.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Estimate {estimate.estimateId}
        </Typography>
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PdfIcon />}
              onClick={handleGeneratePDF}
              data-pdf-button
            >
              Download PDF
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={portalLinkCopied ? <CheckCircleIcon /> : <LinkIcon />}
              onClick={handleCopyPortalLink}
            >
              {portalLinkCopied ? 'Portal Link Copied!' : 'Copy Portal Link'}
            </Button>
            {!estimate.signatureUrl && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CreateIcon />}
                onClick={() => setSignatureDialogOpen(true)}
              >
                Sign Estimate
              </Button>
            )}
            {estimate.signatureUrl && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckCircleIcon />}
                disabled
              >
                Signed
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={<WorkIcon />}
              onClick={handleCreateJob}
            >
              Convert to Job
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/estimates/${estimate.id}/edit`)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Customer Information</Typography>
            <Typography><strong>Name:</strong> {estimate.customerName}</Typography>
            <Typography><strong>Email:</strong> {estimate.contactInfo.email}</Typography>
            <Typography><strong>Phone:</strong> {estimate.contactInfo.phone}</Typography>
            <Typography><strong>Address:</strong> {estimate.contactInfo.streetAddress}</Typography>
            {estimate.contactInfo.city && (
              <Typography>{estimate.contactInfo.city}, {estimate.contactInfo.zipCode}</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Estimate Details</Typography>
            <Typography><strong>Status:</strong> {estimate.status}</Typography>
            <Typography><strong>Total:</strong> ${estimate.total.toFixed(2)}</Typography>
            <Typography><strong>Created:</strong> {formatDate(estimate.createdAt)}</Typography>
            <Typography><strong>Description:</strong> {estimate.description || 'N/A'}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Signature Section - Separate Card */}
      <Paper sx={{ p: 3, mt: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Customer Signature</Typography>
        <Divider sx={{ mb: 2 }} />
        {estimate.signatureUrl ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
              <Box
                component="img"
                src={estimate.signatureUrl.startsWith('data:') ? estimate.signatureUrl : ensureHttps(estimate.signatureUrl)}
                alt="Customer signature"
                sx={{
                  maxWidth: '200px',
                  maxHeight: '100px',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                  bgcolor: 'background.paper',
                  objectFit: 'contain'
                }}
              />
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Signed by:</strong> {estimate.signedByName || 'Customer'}
                </Typography>
                {estimate.signedAt && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <strong>Signed on:</strong> {formatDate(estimate.signedAt)}
                  </Typography>
                )}
              </Box>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CreateIcon />}
              onClick={() => setSignatureDialogOpen(true)}
              sx={{ alignSelf: 'flex-start' }}
            >
              Update Signature
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This estimate has not been signed yet.
            </Typography>
            <Button
              variant="contained"
              size="medium"
              startIcon={<CreateIcon />}
              onClick={() => setSignatureDialogOpen(true)}
            >
              Sign Estimate
            </Button>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">System Information</Typography>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Created By</Typography>
              <Typography variant="body1">{estimate.createdBy}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Created At</Typography>
              <Typography variant="body1">{formatDate(estimate.createdAt)}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Last Updated</Typography>
              <Typography variant="body1">{formatDate(estimate.updatedAt)}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Line Items</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimate.lineItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.description || ''}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">${item.unitPrice.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    ${(item.quantity * item.unitPrice).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={4} align="right">
                  <Typography variant="h6">Total</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="h6">
                    ${estimate.total.toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" color="text.secondary">Estimate Description</Typography>
        <Typography variant="body1">{estimate.description}</Typography>
      </Paper>

      {/* Hidden PDF content */}
      <div style={{ display: 'none' }}>
        <div
          ref={pdfRef}
          data-pdf-content
          style={{
            width: '564px', // 612 - 2*24 (Letter size with reduced margins)
            minHeight: '744px', // 792 - 2*24
            display: 'block',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: 'Arial, sans-serif',
            boxSizing: 'border-box',
            margin: '0',
            padding: '0',
            overflow: 'visible'
          }}
        >
          <EstimatePDFContent estimate={estimate} />
        </div>
      </div>

      {/* Signature Capture Dialog */}
      <SignatureCapture
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        onSave={handleSaveSignature}
        estimateId={estimate.id}
        existingSignature={estimate.signatureUrl}
      />
    </Box>
  );
} 